import React, { useEffect, useState } from "react";
import { BsPlusLg, BsTrash } from "react-icons/bs";
import { Dropdown } from "react-bootstrap";
// eslint-disable-next-line import/no-extraneous-dependencies
import json5 from "json5";
import { ReplacementGroup } from "../replacement-group";
import {
    FieldTypedValueInput, FormFieldReplacementInput, FormFieldType,
    WorkflowStepKeyValueParameterInput,
} from "../../__generated__/api";
import EditableControl, { FORM_FIELD, ValueTypes } from "./EditableText";
import createId from "../../create-id";
import { isDeepEqual } from "../../api";

export interface JsonProperties {
    value: string | Record<string, unknown>;
    input?: ReplacementGroup[];
    replacements?: FormFieldReplacementInput[];
    onChange: (json: string) => void;
}

interface JsonValue {
    id: string;
    value?: WorkflowStepKeyValueParameterInput;
}

function tryParse(value: string | Record<string, unknown>): Record<string, unknown> | unknown[] | undefined {
    if (typeof value === "string") {
        try {
            return json5.parse(value || "[]");
        } catch {
            try {
                return JSON.parse(`{${value}}` || "[]");
            } catch {
                return json5.parse(`{${value.replace(/\n/g, ",\n")}}` || "[]");
            }
        }
    }
    return value;
}

function tryParseValue(value: string | Record<string, unknown>, replacements?: FormFieldReplacementInput[]) {
    const list = tryParse(value);
    if (Array.isArray(list)) {
        return (list as WorkflowStepKeyValueParameterInput[]).map(row => ({
            id: createId(),
            value: row,
        }));
    } if (typeof list === "object") {
        return Object.entries(list).map(([key, objectValue]) => {
            let type = FormFieldType.Text;
            if (typeof objectValue === "number") {
                type = FormFieldType.Number;
            } else if (typeof objectValue === "boolean") {
                type = FormFieldType.Boolean;
            }
            return {
                id: createId(),
                value: {
                    key,
                    replacement: replacements,
                    value: {
                        fieldId: "urn:fq:form-field:none",
                        type,
                        string: objectValue as string,
                    },
                } as WorkflowStepKeyValueParameterInput,
            };
        });
    }
    throw new Error("Invalid JSON");
}

export default function JsonBuilder({
    value, onChange, input, replacements,
}: JsonProperties) {
    const [json, setJson] = useState<JsonValue[]>(() => tryParseValue(value, replacements));
    const [next, setNext] = useState(() => Object.keys(json).filter(key => key.startsWith("Untitled")).length + 1);
    const onAdded = (type: FormFieldType) => {
        setNext(next + 1);
        setJson([
            ...json,
            {
                id: createId(),
                value: {
                    key: `Untitled_${next}`,
                    value: {
                        fieldId: "urn:fq:form-field:none",
                        type,
                    },
                },
            },
        ]);
    };
    const onRemove = (id: string) => {
        const copy = [...json.filter(row => row.id !== id)];
        setJson(copy);
    };

    useEffect(() => {
        onChange(JSON.stringify(json.map(row => row.value)));
    }, [json]);

    useEffect(() => {
        const jsonValue = tryParseValue(value, replacements);
        if (!isDeepEqual(json.toIdList("value"), jsonValue.toIdList("value"))) {
            setJson(jsonValue);
        }
    }, [value]);
    return (
        <div>

            <Dropdown className="p-0">
                <Dropdown.Toggle
                    variant=""
                    className="btn-none btn text-gray-500 ms-0 px-0"
                >
                    <span className="px-1 rounded-pill hover-shadow">
                        <BsPlusLg size="15" />
                    </span>
                    {" "}
                    Add Value
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    <Dropdown.Item onClick={() => onAdded(FormFieldType.Text)}>
                        Text
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => onAdded(FormFieldType.LongText)}>
                        Long Text
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => onAdded(FormFieldType.Number)}>
                        Number
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => onAdded(FormFieldType.Boolean)}>
                        Boolean
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => onAdded(FormFieldType.Date)}>
                        Date
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => onAdded(FormFieldType.DateTime)}>
                        Date/Time
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>
            <div className="list-group">
                {json.map(child => (
                    <div className="list-group-item" key={child.id}>
                        <div className="row">
                            <div className="col-4 ps-0">
                                <input
                                    type="text"
                                    className="form-control h-100"
                                    placeholder="Key"
                                    value={child.value?.key ?? ""}
                                    onChange={event => {
                                        const newJson = [...json];
                                        const rowValue = newJson.find(row => row === child) ?? {} as JsonValue;
                                        rowValue.value ??= {} as WorkflowStepKeyValueParameterInput;
                                        rowValue.value.key = event.target.value;
                                        setJson(newJson);
                                    }}
                                />
                            </div>
                            <div className="col px-0 d-flex">
                                <EditableControl
                                    inputClassName="form-control py-1"
                                    placeholderClassName="text-muted py-0"
                                    variableClassName="p-2 ms-2 my-0"
                                    placeholder="Value"
                                    replaceableInput={input}
                                    isFormField
                                    shouldFocus={false}
                                    type={FORM_FIELD[child.value?.value.type ?? FormFieldType.Text]}
                                    replacements={child?.value?.replacement as FormFieldReplacementInput[] ?? []}
                                    value={child?.value?.value.string as ValueTypes}
                                    isEditing
                                    onChange={(newValue, replace) => {
                                        const newJson = [...json];
                                        const rowValue = newJson.find(row => row === child) ?? {} as JsonValue;
                                        rowValue.value ??= {} as WorkflowStepKeyValueParameterInput;
                                        rowValue.value.value ??= {} as FieldTypedValueInput;
                                        rowValue.value.replacement = replace;
                                        if (typeof newValue === "string") {
                                            rowValue.value.value.string = newValue;
                                        } else if (typeof newValue === "number") {
                                            rowValue.value.value.number = newValue;
                                        } else if (typeof newValue === "boolean") {
                                            rowValue.value.value.boolean = newValue;
                                        } else if (newValue instanceof Date) {
                                            rowValue.value.value.date = newValue;
                                        }
                                        setJson(newJson);
                                    }}
                                />
                                <button
                                    className="text-light btn btn-none"
                                    type="button"
                                    onClick={() => onRemove(child.id)}
                                >
                                    <BsTrash />
                                </button>

                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

JsonBuilder.defaultProps = {
    input: undefined,
    replacements: undefined,
};
